import {
  addTrailingSlash,
  createRouteParams
} from "@prismicio/editor-support/URL"
import type { Document } from "@prismicio/types-internal/lib/content"
import { useCallback, useEffect } from "react"
import {
  useNavigate,
  useParams as useReactRouterParams,
  useSearchParams as useReactRouterSearchParams
} from "react-router-dom"
import { z } from "zod"

import { env } from "./env"

class ParamsValidationError extends Error {
  constructor(zodError: z.ZodError) {
    super(zodError.toString())
  }
}

export function isParamsValidationError(
  error: unknown
): error is ParamsValidationError {
  return error instanceof ParamsValidationError
}

/**
 * Validates react-router-dom path and search params.
 */

export const useRouteParams = <
  PathShape extends z.ZodRawShape,
  SearchShape extends z.ZodRawShape
>({
  path = {} as PathShape,
  search = {} as SearchShape
}: {
  path?: PathShape
  search?: SearchShape
}) => {
  const params = useReactRouterParams()
  const validatedPath = z.object(path ?? {}).safeParse(params)
  if (!validatedPath.success)
    throw new ParamsValidationError(validatedPath.error)

  const searchParams = Object.fromEntries(useReactRouterSearchParams()[0])
  const validatedSearch = z.object(search ?? {}).safeParse(searchParams)
  if (!validatedSearch.success)
    throw new ParamsValidationError(validatedSearch.error)

  return {
    path: validatedPath.data,
    search: validatedSearch.data
  }
}

interface GoToNewDocumentParams {
  customTypeId: string
  groupLangId?: string
  locale: string
  initialDraft?: Document
  tags?: string[]
  title?: string
}
interface GoToDocumentArgs {
  releaseId?: string
}

interface GoToDocumentArgs {
  releaseId?: string
}

export function useGoToNewDocument(args?: GoToDocumentArgs) {
  const { releaseId } = args || {}
  const navigate = useNavigate()

  return useCallback(
    (params: GoToNewDocumentParams) => {
      const { customTypeId, groupLangId, locale, initialDraft, tags, title } =
        params

      const routeParams = {
        custom_type: customTypeId,
        group_lang_id: groupLangId,
        r: releaseId,
        locale
      }
      const newSearchParams = createRouteParams({ params: routeParams })

      const searchParams = new URLSearchParams(newSearchParams).toString()

      const to = `/pages/new?${searchParams}`
      navigate(to, {
        replace: false,
        state: { document: initialDraft, tags, title }
      })
    },
    [navigate, releaseId]
  )
}

export function getRepositoryName(): string {
  let repositoryName: string | undefined
  if (env.VERCEL_URL !== undefined) {
    repositoryName = undefined
  } else {
    const labels = location.hostname.split(".")
    repositoryName = labels.length === 3 ? labels[0] : undefined
  }

  repositoryName ??= env.PAGE_BUILDER_FALLBACK_REPOSITORY_NAME

  if (repositoryName === undefined) {
    throw new Error(
      `Cannot get the repository name from the current \`${location.href}\` location.`
    )
  }

  return repositoryName
}

export function getBaseUrl(): URL {
  return addTrailingSlash(new URL(import.meta.env.BASE_URL, location.origin))
}

export function getRouterBasename(): string {
  return getBaseUrl().pathname
}

export function hasNestedBaseUrl(): boolean {
  return getBaseUrl().pathname !== "/"
}

export function useRepositoryMetaTitle(pageName: string) {
  const repositoryName = getRepositoryName()

  useEffect(() => {
    document.title = `${repositoryName} - ${pageName}`
  }, [pageName, repositoryName])
}

const domain = env.PRISMIC_DOMAIN

interface GetBaseRepositoryUrlArgs {
  repositoryId: string
}

export function getBaseRepositoryUrl(args: GetBaseRepositoryUrlArgs) {
  const { repositoryId } = args

  return `https://${repositoryId}.${domain}`
}
